import { ArrowBack, Download, Upload } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { DriverEntity } from 'src/entities/Driver.entity';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import { TruckShiftEntity } from 'src/entities/TruckShift.entity';
import { ImportCommitResult, ImportPreviewResult } from 'src/hooks/useMutationTruckShiftImport';
import { GridSelection } from 'src/pages/TruckShifts/bulkSelection';
import { AddRequestBody, EditRequestBody } from 'src/pages/TruckShifts/cellEditor';
import { PatternFormValues } from 'src/pages/TruckShifts/patternForm';
import { CellEditorPopover } from 'src/pages/TruckShifts/presenters/CellEditorPopover.presenter';
import { CopyDialog } from 'src/pages/TruckShifts/presenters/CopyDialog.presenter';
import { ImportDialog } from 'src/pages/TruckShifts/presenters/ImportDialog.presenter';
import { OverwriteConfirmDialog } from 'src/pages/TruckShifts/presenters/OverwriteConfirmDialog.presenter';
import { PatternManagerDialog } from 'src/pages/TruckShifts/presenters/PatternManagerDialog.presenter';
import { SelectionBar } from 'src/pages/TruckShifts/presenters/SelectionBar.presenter';
import { TablePresenter } from 'src/pages/TruckShifts/presenters/Table.presenter';
import { ToolbarPresenter } from 'src/pages/TruckShifts/presenters/Toolbar.presenter';
import { DateRange, FilterOptions, PeriodMode, ShiftCell, TruckFilters } from 'src/pages/TruckShifts/truckShiftGrid';

// リリース時はインポート・エクスポートを一旦無効化する（PM フィードバック）。
// UI・ロジック・API は残してあるので、再開時はこのフラグを true に戻すだけでよい。
const ENABLE_IMPORT_EXPORT: boolean = false;

type Props = {
  isLoading: boolean;
  isError: boolean;
  trucks: TruckShiftEntity[];
  filteredTrucks: TruckShiftEntity[];
  dateColumns: string[];
  period: PeriodMode;
  rangeLabel: string;
  filterOptions: FilterOptions;
  filters: TruckFilters;
  onPeriodChange: (period: PeriodMode) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onFiltersChange: (filters: TruckFilters) => void;
  onBack?: () => void;
  patternManagerOpen: boolean;
  onExport: () => void;
  exportDisabled: boolean;
  onOpenImport: () => void;
  importDisabled: boolean;
  importOpen: boolean;
  onCloseImport: () => void;
  runImport: (args: { file: File; commit: boolean }) => Promise<ImportPreviewResult | ImportCommitResult>;
  extractImportError: (error: unknown) => string;
  onImportNotify: (message: string, variant: 'success' | 'error') => void;
  onClosePatternManager: () => void;
  patterns: ShiftPatternEntity[];
  patternSubmitting: boolean;
  onSubmitPattern: (values: PatternFormValues, editing?: ShiftPatternEntity) => Promise<boolean>;
  onDeletePattern: (pattern: ShiftPatternEntity) => Promise<boolean>;
  selection: GridSelection;
  selectedDates: Set<string>;
  onDatesChange: (dates: Set<string>) => void;
  // TablePresenter 配下の memo 化コンポーネント（CompanyGroup/BodyRow）には selection オブジェクトではなく
  // 実体の Set をそのまま渡す（selection は selectedTruckIds/selectedDates のどちらかが変わるたびに
  // 新しい参照になり、配下の全再レンダーを引き起こすため）。
  selectedTruckIds: Set<number>;
  selectedTruckCount: number;
  activePatternId: number | null;
  onPatternChange: (patternId: number) => void;
  onAddPattern: () => void;
  onManagePatterns: () => void;
  applying: boolean;
  onApply: () => void;
  onRemove: () => void;
  removing: boolean;
  removeCount: number;
  onCellClick: (truckId: number, date: string, cells: ShiftCell[], anchorEl: HTMLElement) => void;
  copy: {
    visible: boolean;
    disabled: boolean;
    open: boolean;
    source: DateRange;
    target: DateRange;
    truckCount: number;
    existingCount: number;
    busy: boolean;
    onOpen: () => void;
    onClose: () => void;
    onCopy: () => void;
  };
  overwriteConfirm: {
    open: boolean;
    action: 'apply' | 'remove';
    shiftCount: number;
    partialMultiDayCount: number;
    splitAvailable: boolean;
    wholeAvailable: boolean;
    blockedCount: number;
    truckCount: number;
    dateCount: number;
    applying: boolean;
    onSplit: () => void;
    onWhole: () => void;
    onCancel: () => void;
  };
  cellPopover: {
    open: boolean;
    anchorEl: HTMLElement | null;
    truckId: number;
    date: string;
    truckName: string;
    cells: ShiftCell[];
    patternOptions: ShiftPatternEntity[];
    allPatterns: ShiftPatternEntity[];
    driverOptions: DriverEntity[];
    onClose: () => void;
    editShift: (id: number, body: EditRequestBody) => Promise<void>;
    assignDriver: (id: number, driverId: number | null) => Promise<void>;
    createDriver: (name: string) => Promise<{ id: number; name: string }>;
    addShift: (body: AddRequestBody) => Promise<void>;
    deleteShift: (id: number) => Promise<void>;
    truncateShift: (id: number, on: string) => Promise<void>;
    onNotify: (message: string, variant: 'success' | 'error') => void;
    extractError: (rejection: PromiseRejectedResult) => string;
  };
};

export const TruckShiftsPresenter: FC<Props> = ({
  isLoading, isError, trucks, filteredTrucks, dateColumns,
  period, rangeLabel, filterOptions, filters,
  onPeriodChange, onPrev, onNext, onToday, onFiltersChange, onBack,
  patternManagerOpen, onClosePatternManager,
  patterns, patternSubmitting, onSubmitPattern, onDeletePattern,
  selection, selectedDates, onDatesChange, selectedTruckIds, selectedTruckCount,
  activePatternId, onPatternChange, onAddPattern, onManagePatterns,
  applying, onApply, onRemove, removing, removeCount,
  onCellClick, cellPopover, copy, overwriteConfirm,
  onExport, exportDisabled, onOpenImport, importDisabled, importOpen, onCloseImport,
  runImport, extractImportError, onImportNotify,
}) => {
  const renderBody = () => {
    if (isLoading) {
      return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }
    if (isError) {
      return <Alert severity="error" sx={{ m: 2 }}>勤務計画の取得に失敗しました</Alert>;
    }
    if (trucks.length === 0) {
      return <Typography sx={{ p: 2 }} color="text.secondary">表示できるトラックがありません</Typography>;
    }
    if (filteredTrucks.length === 0) {
      return <Typography sx={{ p: 2 }} color="text.secondary">条件に一致するトラックがありません</Typography>;
    }
    return (
      <TablePresenter
        trucks={filteredTrucks}
        dateColumns={dateColumns}
        selection={selection}
        selectedTruckIds={selectedTruckIds}
        selectedDates={selectedDates}
        onCellClick={onCellClick}
      />
    );
  };

  return (
    <>
      <title>勤務計画（トラック起点）</title>
      <Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          {onBack && (
            <>
              <Button
                size="small"
                startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                onClick={onBack}
                sx={{ textTransform: 'none' }}
              >
                配車計画に戻る
              </Button>
              <Divider orientation="vertical" flexItem />
            </>
          )}
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>稼働表</Typography>
          </Box>
          <Box flexGrow={1} />
          {ENABLE_IMPORT_EXPORT && (
            <>
              <Button size="small" variant="outlined" startIcon={<Upload sx={{ fontSize: 16 }} />} onClick={onOpenImport} disabled={importDisabled}>インポート</Button>
              <Button size="small" variant="outlined" startIcon={<Download sx={{ fontSize: 16 }} />} onClick={onExport} disabled={exportDisabled}>エクスポート</Button>
            </>
          )}
        </Box>
        <ToolbarPresenter
          period={period}
          rangeLabel={rangeLabel}
          filterOptions={filterOptions}
          filters={filters}
          onPeriodChange={onPeriodChange}
          onPrev={onPrev}
          onNext={onNext}
          onToday={onToday}
          onFiltersChange={onFiltersChange}
          onOpenCopy={copy.onOpen}
          copyVisible={copy.visible}
          copyDisabled={copy.disabled}
        />
        {!isLoading && !isError && trucks.length > 0 && (
          <SelectionBar
            truckCount={selectedTruckCount}
            dateColumns={dateColumns}
            selectedDates={selectedDates}
            onDatesChange={onDatesChange}
            patterns={patterns}
            activePatternId={activePatternId}
            onPatternChange={onPatternChange}
            onAddPattern={onAddPattern}
            onManagePatterns={onManagePatterns}
            applying={applying}
            onApply={onApply}
            onRemove={onRemove}
            removing={removing}
            removeCount={removeCount}
          />
        )}
        {renderBody()}
      </Stack>
      <PatternManagerDialog
        open={patternManagerOpen}
        submitting={patternSubmitting}
        patterns={patterns}
        onClose={onClosePatternManager}
        onSubmitPattern={onSubmitPattern}
        onDeletePattern={onDeletePattern}
      />
      <CellEditorPopover {...cellPopover} />
      {ENABLE_IMPORT_EXPORT && (
        <ImportDialog
          open={importOpen}
          onClose={onCloseImport}
          runImport={runImport}
          onNotify={onImportNotify}
          extractError={extractImportError}
        />
      )}
      <CopyDialog
        open={copy.open}
        source={copy.source}
        target={copy.target}
        truckCount={copy.truckCount}
        existingCount={copy.existingCount}
        busy={copy.busy}
        onClose={copy.onClose}
        onCopy={copy.onCopy}
      />
      <OverwriteConfirmDialog
        open={overwriteConfirm.open}
        action={overwriteConfirm.action}
        shiftCount={overwriteConfirm.shiftCount}
        partialMultiDayCount={overwriteConfirm.partialMultiDayCount}
        splitAvailable={overwriteConfirm.splitAvailable}
        wholeAvailable={overwriteConfirm.wholeAvailable}
        blockedCount={overwriteConfirm.blockedCount}
        truckCount={overwriteConfirm.truckCount}
        dateCount={overwriteConfirm.dateCount}
        applying={overwriteConfirm.applying}
        onSplit={overwriteConfirm.onSplit}
        onWhole={overwriteConfirm.onWhole}
        onCancel={overwriteConfirm.onCancel}
      />
    </>
  );
};
